export interface QrTemplateBusinessInfo {
  name: string;
  whatsappNumber: string;
  phoneNumber?: string;
  email?: string;
}

export type QrMode = 'whatsapp' | 'vcard';

export interface QrTemplateColors {
  background: string;
  surface: string;
  text: string;
  textMuted: string;
  accent: string;
  onAccent: string;
}

export type PartialColors = Partial<QrTemplateColors>;

export interface EditableSlot {
  id: string;
  label: string;
  defaultValue: string;
  modeVariants?: { whatsapp?: string; vcard?: string };
  multiline?: boolean;
  positionable?: boolean;
}

export interface SlotOffset {
  x: number;
  y: number;
}

export interface QrTemplateContext {
  ctx: CanvasRenderingContext2D;
  canvas: HTMLCanvasElement;
  qrImage: HTMLImageElement;
  business: QrTemplateBusinessInfo;
  mode: QrMode;
  colors: QrTemplateColors;
  slotValues: Record<string, string>;
  slotOffsets: Record<string, SlotOffset>;
}

export interface QrTemplate {
  id: string;
  name: string;
  description: string;
  format: string;
  canvasWidth: number;
  canvasHeight: number;
  defaultColors: QrTemplateColors;
  slots: EditableSlot[];
  render: (context: QrTemplateContext) => void;
}

export function mergeColors(defaults: QrTemplateColors, override?: PartialColors): QrTemplateColors {
  return {
    background: override?.background || defaults.background,
    surface: override?.surface || defaults.surface,
    text: override?.text || defaults.text,
    textMuted: override?.textMuted || defaults.textMuted,
    accent: override?.accent || defaults.accent,
    onAccent: override?.onAccent || defaults.onAccent,
  };
}

export function resolveSlotDefault(slot: EditableSlot, mode: QrMode): string {
  if (slot.modeVariants) {
    const variant = mode === 'whatsapp' ? slot.modeVariants.whatsapp : slot.modeVariants.vcard;
    if (variant !== undefined) return variant;
  }
  return slot.defaultValue;
}

export function resolveSlotValues(
  slots: EditableSlot[],
  mode: QrMode,
  overrides?: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const slot of slots) {
    const override = overrides?.[slot.id];
    out[slot.id] = override !== undefined ? override : resolveSlotDefault(slot, mode);
  }
  return out;
}

export function resolveSlotOffsets(
  slots: EditableSlot[],
  overrides?: Record<string, SlotOffset>,
): Record<string, SlotOffset> {
  const out: Record<string, SlotOffset> = {};
  for (const slot of slots) {
    out[slot.id] = overrides?.[slot.id] ?? { x: 0, y: 0 };
  }
  return out;
}
